import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Clock, Newspaper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/news`)
      .then(res => setArticles(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-16">
      {/* PAGE HERO */}
      <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="news-hero">
        <div className="absolute inset-0 opacity-10" aria-hidden="true" />
        <div className="relative container-institutional">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Actualités</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">Dernières Nouvelles</h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">Suivez toute l'actualité du Forum URBANIK 2026.</p>
        </div>
      </section>

      {/* ARTICLES GRID */}
      <section className="py-20 lg:py-28" data-testid="articles-grid">
        <div className="container-institutional">
          {loading ? (
            <div className="text-center py-12 text-[#94A3B8]">Chargement des articles...</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper className="w-16 h-16 text-[#E1E8F0] mx-auto mb-4" />
              <p className="text-lg text-[#6B7C93]">Aucun article publié pour le moment.</p>
              <p className="text-sm text-[#94A3B8] mt-2">Revenez bientôt pour les dernières nouvelles d'URBANIK 2026.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(article => (
                <article
                  key={article.id}
                  className="bg-white border border-[#E1E8F0] rounded-lg overflow-hidden hover:shadow-md transition-shadow group cursor-pointer"
                  onClick={() => setExpanded(expanded === article.id ? null : article.id)}
                  data-testid={`article-${article.id}`}
                >
                  {article.image_url && (
                    <div className="overflow-hidden aspect-video">
                      <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#F5A623]">{article.category}</span>
                      <span className="text-xs text-[#94A3B8] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(article.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-[#1E3A5F] mb-2 group-hover:text-[#F5A623] transition-colors leading-snug">{article.title}</h3>
                    <p className="text-sm text-[#6B7C93] leading-relaxed mb-2">{article.excerpt}</p>
                    {expanded === article.id && article.content && (
                      <div className="mt-4 pt-4 border-t border-[#E1E8F0]">
                        <p className="text-sm text-[#4A5568] leading-relaxed whitespace-pre-line">{article.content}</p>
                      </div>
                    )}
                    {article.content && (
                      <span className="text-sm font-medium text-[#1E3A5F] hover:text-[#F5A623] transition-colors flex items-center gap-1 mt-2">
                        {expanded === article.id ? 'Réduire' : 'Lire la suite'} <ArrowRight className={`w-4 h-4 transition-transform ${expanded === article.id ? 'rotate-90' : ''}`} />
                      </span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#1E3A5F]" data-testid="news-cta">
        <div className="container-institutional text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Restez informé</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">Ne manquez aucune annonce liée au Forum URBANIK 2026.</p>
          <Link to="/participer" className="btn-primary text-base px-8 py-4" data-testid="news-cta-btn">S'inscrire <ArrowRight className="w-5 h-5 ml-2" /></Link>
        </div>
      </section>
    </div>
  );
};

export default News;
